import React, { useState, MouseEvent } from 'react';
import { Collapse } from 'react-collapse';
import clsx from 'clsx';
import { useSelector } from 'react-redux';

import Checkbox from '@components/Checkbox';
import { atLeastOneEqual, convertNumber, getLabelFallback, labels } from '@utils/utils';
import { WishlistItem } from '@store/wishlist/wishlistInterfaces';
import ArrowDownIcon from '@images/arrow-down.svg';
import ButtonLink from '@components/ButtonLink';
import {
  selectConfig,
  selectEditSavedItemsGlassesWarrantyStatus,
} from '../../store/wishlist/wishlistSlice';
import Notice from '@components/Notice';

interface Props {
  item: WishlistItem;
  isWarrantyActive: boolean;
  handleWarrentyToggle: (isActive: boolean) => void;
  handleGoToWarrantyPolicy: (e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => void;
  isInStock: boolean;
}

export default function WishlistFrameItemContent({
  item,
  isWarrantyActive,
  handleWarrentyToggle,
  handleGoToWarrantyPolicy,
  isInStock,
}: Props) {
  const editGlassesWarrantyStatus = useSelector(selectEditSavedItemsGlassesWarrantyStatus);
  const config = useSelector(selectConfig);

  const [showPrescription, setShowPrescription] = useState(false);

  const lens = item.catentries.find(entry => entry.catEntryType === 'LENS');
  const frame = item.catentries.find(entry => atLeastOneEqual(entry.catEntryType, ['SUN', 'EYE']));
  const isFrameOnly = !lens || convertNumber(lens.listPrice) === '0.00';
  const lensAttributes: any = !isFrameOnly && lens.attributes.find((attr: Attr) => attr.name === "rxc_obj").value;
  const lensAttributesList: any = lensAttributes ? Object.values(JSON.parse(lensAttributes)) : [];

  const warrantyPrice = config.brand === 'TO' ? 35.0 : 34.99;

  const handleEditLenses = () => {
    const pageUrl = frame.attributes.find(attr => attr.name === 'pageUrl').value as string;

    let splittedUrl = pageUrl.split('&');
    const index = splittedUrl.findIndex(str => str.includes('productId'));
    splittedUrl[index] = 'productId=' + frame.catEntryId;
    const joinedUrl = splittedUrl.join('&');
    const bundleRXC = (item.isBundle ? '&isBundle=true' : '');

    if (pageUrl) window.location.href = `${config.hostname}${joinedUrl}${bundleRXC}&openRx=true`;
  };

  return (
    <>
      {frame.attributes.some(attr => attr.name === 'color') && (
        <p className="wishlist-item-color">
          <b>{getLabelFallback('WISHLIST_FRAME_COLOR')}:</b>{' '}
          {frame.attributes.find(attr => attr.name === 'color').value}
        </p>
      )}
      {frame.attributes.some(attr => attr.name === 'size') && (
        <p className="wishlist-item-size">
          <b>{getLabelFallback('WISHLIST_FRAME_SIZE')}:</b>{' '}
          {frame.attributes.find(attr => attr.name === 'size').value}
        </p>
      )}
      {config.brand === 'TO' &&
        isInStock &&
        item.catentries.find(entry => entry.catEntryType === 'SUN') &&
        !isFrameOnly && (
          <Notice text={getLabelFallback('WISHLIST_MISSING_PRESCRIPTION')} type="warning" />
        )}
      {!(isFrameOnly && config.brand === 'LC') ? (
        <>
          <div className="wishlist-item-lenses-color-container">
            {!isFrameOnly ? (
              <>
                <div className="wishlist-item-lenses-color-content with-lenses">
                  <button
                    className="wishlist-item-lenses-color-title plain"
                    onClick={() => setShowPrescription(!showPrescription)}
                  >
                    <b>
                      {getLabelFallback(
                        ((config.brand === 'TO' ? '' : 'LC_') + 'WISHLIST_LENSES_COLOR') as labels
                      )}
                    </b>
                    <ArrowDownIcon className={clsx(showPrescription && 'rotate')} />
                  </button>
                  <Collapse isOpened={showPrescription}>
                    {config.brand === 'TO' && (
                      <ButtonLink onClick={handleEditLenses}>
                        {getLabelFallback('WISHLIST_EDIT_LENSES')}
                      </ButtonLink>
                    )}
                  </Collapse>
                  {isInStock && (
                    <div className="wishlist-item-lenses-price">
                      {lens?.listPrice !== lens?.offerPrice && (
                        <label className="wishlist-item-subtotal-price lens">
                          {getLabelFallback('CURRENCY_LABEL')}
                          {parseFloat(lens.listPrice).toFixed(2)}
                        </label>
                      )}
                      <label className="wishlist-item-subtotal-price-discounted">
                        {getLabelFallback('CURRENCY_LABEL')}
                        {parseFloat(lens.offerPrice).toFixed(2)}
                      </label>
                    </div>
                  )}
                </div>
                <Collapse isOpened={showPrescription}>
                  <div className="wishlist-item-lenses-prescription">
                    {lensAttributes && lensAttributesList.map((p, index) => {
                  if (p.name === 'Prescription') return; // do not display prescription info
                  return (
                      <div className='wishlist-item-lenses-description-container'>
                        <div className='wishlist-item-lenses-name-container'>
                          <span key={'your-lenses-name' + index} className={'name name-' + index}>
                            {p.name}
                          </span>
                        </div>
                        <div className='wishlist-item-lenses-description-container'>
                          { !!p.description &&
                            <span key={'your-lenses-right' + index} className={'description right-' + index}>
                              {p.description}
                            </span> 
                          }    
                          { !!p.children &&
                            <div key={'your-lenses-right' + index} className={'children right-' + index}>
                              {p.children.map((child, index2) => {
                                return (
                                  <span key={'cart-product-tile-details-label-' + index + '-child-' + index2}>
                                    {child.name}
                                  </span>
                                );
                              })}
                            </div>
                          }
                        </div>
                      </div>
                    );
                    })}
                    {config.brand === 'LC' &&
                      item.catentries.find(entry => entry.catEntryType === 'SUN') &&
                      !isFrameOnly && (
                        <Notice
                          text={getLabelFallback('WISHLIST_MISSING_PRESCRIPTION')}
                          type="warning"
                        />
                      )}
                  </div>
                </Collapse>
              </>
            ) : (
              <div className="wishlist-item-lenses-color-content">
                <p className="wishlist-item-lenses-color">
                  <b>{getLabelFallback('WISHLIST_LENSES_COLOR')}: </b>
                  {frame.attributes.find(attr => attr.name === 'lensColor')?.value ??
                    getLabelFallback('WISHLIST_NO_PRESCRIPTION')}
                </p>
                {config.brand === 'TO' && (
                  <button
                    className="btn btn-primary btn-add-lens"
                    onClick={handleEditLenses}
                    data-element-id={config.brand === 'TO' ? 'WishiIst_AddLens' : 'Empty_AddLens'}
                  >
                    {getLabelFallback(
                      frame.catEntryType === 'EYE'
                        ? 'WISHLIST_SELECT_LENSES'
                        : 'WISHLIST_SELECT_PRESCRIPTION_LENSES'
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
          {isInStock && (
            <div className="wishlist-item-checkbox-container">
              <Checkbox
                id={item.idList + '-protection-plan'}
                toggleCheckbox={() => handleWarrentyToggle(!isWarrantyActive)}
                checked={isWarrantyActive}
                enabled={editGlassesWarrantyStatus !== 'LOADING'}
              >
                {getLabelFallback('WISHLIST_PROTECTION_PLAN')}
                <a
                  href="#"
                  className="link"
                  onClick={e => {
                    handleGoToWarrantyPolicy(e);
                  }}
                  data-element-id="Prods_ProtectionPlan"
                >
                  {getLabelFallback(
                    ((config.brand === 'TO' ? '' : 'LC_') +
                      'WISHLIST_PROTECTION_PLAN_LINK') as labels
                  )}
                </a>
              </Checkbox>
              <p className="wishlist-item-checkbox-price">
                {getLabelFallback('WISHLIST_PROTECTION_PLAN_PRICE').replace(
                  '##WARRANTY_PRICE##',
                  warrantyPrice.toString()
                )}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="separator" />
      )}
    </>
  );
}
